import React, { useEffect, useState } from "react";
import {
    Box, Chip, Dialog, Divider, IconButton, Stack, Tooltip, Typography,
} from "@mui/material";
import dayjs from "dayjs";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import NotesRoundedIcon from "@mui/icons-material/NotesRounded";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import ApartmentRoundedIcon from "@mui/icons-material/ApartmentRounded";
import AttachFileRoundedIcon from "@mui/icons-material/AttachFileRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";

import AttachmentPreviewList from "../../../component/common/AttachmentPreviewList";
import { calendarApi } from "../utils/calendarApi";
import { asList, isProjectEvent } from "../utils/calendarUtils";
import { categoryKeyOf, categoryMeta } from "../constants/eventCategory";

/**
 * 일정 <b>읽기전용 상세</b> 팝업.
 *
 * <h3>왜 이 화면이 따로 있나</h3>
 * 예전에는 일정을 클릭하면 곧바로 <b>등록·수정 폼</b>이 열렸다(작성자가 아니면 입력만 잠긴 같은 폼).
 * 일정은 <b>보는 일이 고치는 일보다 훨씬 잦은데</b> 매번 폼이 떠서, 내용을 확인하려 눌렀다가
 * 값을 건드릴 위험이 있고 화면도 무거웠다. 그래서 클릭 = 읽기, 고치려면 한 번 더 = 수정으로 나눈다.
 *
 * <p>수정·삭제 버튼은 <b>작성자에게만</b> 보인다({@code canEdit}). 남의 일정·휴가·프로젝트 합성
 * 일정은 보기만 된다. 최종 판정은 어차피 서버가 하므로 여기 버튼 유무는 실수 방지용이다.
 */
/**
 * 표시 크기 — 한 곳에서 조정한다.
 *
 * <p>본문이 작아 읽기 불편하다는 피드백으로 기존 대비 <b>본문 1.3배</b>, 제목은 그보다 더 키웠다.
 * 상세는 '읽는 화면' 이라 입력 폼(EventDialog)의 촘촘한 크기를 따라갈 이유가 없다.
 * 숫자를 바꿀 때는 아이콘·간격도 같이 움직여야 줄 정렬이 흐트러지지 않으므로 여기 묶어 둔다.
 */
const UI = {
    title: 19,        // 제목
    period: 15,       // 제목 아래 기간
    body: 15,         // 설명·부서·참여자·유형 등 본문
    chip: 13,         // 참여자 칩 글자
    chipH: 24,        // 참여자 칩 높이
    meta: 13.5,       // 등록·최종 수정 줄
    icon: 18,         // 본문 왼쪽 아이콘
    iconCol: 22,      // 아이콘 열 너비 (아이콘보다 커야 줄이 세로로 맞는다)
    dot: 11,          // 유형 색 점
    gutter: 3,        // 좌우 여백 (MUI spacing)
    rowGap: 1.8,      // 본문 줄 간격 (MUI spacing)
};

export function EventDetailModal({ open, event, canEdit, onClose, onEdit, onDelete }) {
    const [attachments, setAttachments] = useState([]);

    // 휴가·프로젝트는 캘린더가 소유하지 않는 합성 이벤트라 첨부가 없다(id 가 "leave-.." 같은 문자열).
    const isSynthetic = Boolean(event?.isLeave || isProjectEvent(event));
    const canLoadAttachments = Boolean(open && event?.id && !isSynthetic);

    useEffect(() => {
        if (!canLoadAttachments) { setAttachments([]); return; }
        let alive = true;
        calendarApi.getAttachments(event.id)
            .then(r => { if (alive) setAttachments(asList(r, "attachments")); })
            .catch(() => { if (alive) setAttachments([]); });
        return () => { alive = false; };
    }, [canLoadAttachments, event?.id]);

    if (!event) return null;

    const cat = categoryMeta(categoryKeyOf(event));
    const participants = Array.isArray(event.participants) ? event.participants : [];

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
            PaperProps={{ sx: {
                borderRadius: 2, maxHeight: "88vh",
                boxShadow: "0 18px 60px rgba(15,23,42,0.16)",
            } }}>
            {/* 헤더 — 유형 색 점 + 제목 + 기간 */}
            <Box sx={{ px: UI.gutter, pt: 2.5, pb: 2 }}>
                <Stack direction="row" alignItems="flex-start" gap={1.2}>
                    <Box sx={{ width: UI.dot, height: UI.dot, borderRadius: "50%", bgcolor: cat.bar,
                        mt: "10px", flexShrink: 0 }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: UI.title, fontWeight: 800, color: "#0f172a", lineHeight: 1.35 }}>
                            {event.title || (event.isLeave ? `${event.leaveType || "휴가"}` : "제목 없음")}
                        </Typography>
                        <Typography sx={{ fontSize: UI.period, color: "#475569", mt: 0.5 }}>
                            {periodText(event)}
                        </Typography>
                    </Box>
                    <IconButton size="small" onClick={onClose} sx={{ mt: -0.5, mr: -1 }}>
                        <CloseRoundedIcon sx={{ fontSize: 24, color: "#94a3b8" }} />
                    </IconButton>
                </Stack>
            </Box>

            {/* 본문 — 값이 있는 줄만 그린다(빈 칸이 늘어서면 무엇이 없는지가 오히려 안 보인다) */}
            <Box sx={{ px: UI.gutter, pb: 2, overflowY: "auto" }}>
                <Stack spacing={UI.rowGap}>
                    {event.description && (
                        <Row icon={<NotesRoundedIcon sx={ICON} />}>
                            <Typography sx={{ fontSize: UI.body, color: "#334155", whiteSpace: "pre-wrap", lineHeight: 1.65 }}>
                                {event.description}
                            </Typography>
                        </Row>
                    )}

                    {event.location && (
                        <Row icon={<PlaceOutlinedIcon sx={ICON} />}>
                            <Typography sx={{ fontSize: UI.body, color: "#334155" }}>
                                {event.location}
                            </Typography>
                        </Row>
                    )}

                    {event.isLeave ? (
                        <Row icon={<PersonRoundedIcon sx={ICON} />}>
                            <Typography sx={{ fontSize: UI.body, color: "#334155" }}>
                                {event.employeeName || "미지정"}
                                {event.deptName ? ` · ${event.deptName}` : ""}
                                {event.leaveType ? ` · ${event.leaveType}` : ""}
                            </Typography>
                        </Row>
                    ) : (
                        event.deptName && (
                            <Row icon={<ApartmentRoundedIcon sx={ICON} />}>
                                <Typography sx={{ fontSize: UI.body, color: "#334155" }}>
                                    {event.deptName}
                                    {event.employeeName ? ` · ${event.employeeName}` : ""}
                                </Typography>
                            </Row>
                        )
                    )}

                    {participants.length > 0 && (
                        <Row icon={<GroupsRoundedIcon sx={ICON} />}>
                            <Stack direction="row" gap={0.6} flexWrap="wrap" useFlexGap>
                                {participants.map((p, i) => (
                                    <Chip key={p.employeeId ?? i} size="small"
                                        label={p.name || "이름 없음"}
                                        title={[p.deptName, p.positionName].filter(Boolean).join(" · ")}
                                        sx={{ height: UI.chipH, fontSize: UI.chip, fontWeight: 500,
                                            bgcolor: "#eff6ff", color: "#1e40af" }} />
                                ))}
                            </Stack>
                        </Row>
                    )}

                    {attachments.length > 0 && (
                        <Row icon={<AttachFileRoundedIcon sx={ICON} />}>
                            <AttachmentPreviewList
                                attachments={attachments}
                                previewFn={calendarApi.previewAttachment}
                                showUploader
                            />
                        </Row>
                    )}

                    {/* 유형 — 화면 어디서나 같은 색을 쓰므로 점 하나로 충분하다 */}
                    <Row icon={<Box sx={{ width: UI.dot, height: UI.dot, borderRadius: "50%",
                        bgcolor: cat.bar, mt: "4px" }} />}>
                        <Typography sx={{ fontSize: UI.body, color: "#334155" }}>
                            {event.categoryLabel || cat.label}
                        </Typography>
                    </Row>
                </Stack>
            </Box>

            <Divider />

            {/* 푸터 — 등록·최종 수정 이력 + (작성자만) 수정·삭제 */}
            <Stack direction="row" alignItems="flex-end" gap={1}
                sx={{ px: UI.gutter, py: 2.2 }}>
                {/* 라벨·사람·시각을 <b>격자</b>로 놓아 두 줄의 열이 정확히 맞는다.
                    Stack 으로 늘어놓으면 '최종 수정'과 '등록'의 글자 길이 차이만큼 뒤 열이 어긋난다.
                    라벨 열은 '최종 수정' 이 들어갈 폭으로 고정한다 — 한 줄만 있을 때도 자리가 같아야
                    수정 전후로 화면이 흔들리지 않는다. */}
                <Box sx={{ flex: 1, minWidth: 0, display: "grid",
                    gridTemplateColumns: "72px auto 1fr", columnGap: 2, rowGap: 1.1, alignItems: "center" }}>
                    {/* 등록 직후엔 updatedAt 이 createdAt 과 같다 — 그때 '최종 수정' 을 띄우면
                        고친 적 없는 일정이 고쳐진 것처럼 보인다. 실제로 달라졌을 때만 보여준다. */}
                    {event.updatedAt && event.updatedAt !== event.createdAt && (
                        <MetaRow label="최종 수정" who={event.createdByName} at={event.updatedAt} />
                    )}
                    {/* 등록자는 항상 — 합성 일정처럼 등록 정보가 없으면 '—' 로라도 자리를 지킨다. */}
                    <MetaRow label="등록" who={event.createdByName} at={event.createdAt} />
                    {isSynthetic && (
                        <Typography sx={{ gridColumn: "1 / -1", fontSize: UI.meta, color: "#94a3b8" }}>
                            {event.isLeave ? "결재(휴가)에서 자동으로 표시되는 일정입니다."
                                           : "프로젝트 채널에서 자동으로 표시되는 일정입니다."}
                        </Typography>
                    )}
                </Box>
                {canEdit && (
                    <Stack direction="row" gap={0.3}>
                        <Tooltip title="수정">
                            <IconButton onClick={onEdit}>
                                <EditRoundedIcon sx={{ fontSize: 22, color: "#64748b" }} />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="삭제">
                            <IconButton onClick={onDelete}>
                                <DeleteOutlineRoundedIcon sx={{ fontSize: 22, color: "#ef4444" }} />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                )}
            </Stack>
        </Dialog>
    );
}

const ICON = { fontSize: UI.icon, color: "#94a3b8" };

/** 아이콘 + 내용 한 줄. 아이콘 열 너비를 고정해 줄들이 세로로 정렬된다. */
function Row({ icon, children }) {
    return (
        <Stack direction="row" gap={1.5} alignItems="flex-start">
            <Box sx={{ width: UI.iconCol, flexShrink: 0, display: "flex", justifyContent: "center", mt: "1px" }}>
                {icon}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
        </Stack>
    );
}

/**
 * 이력 한 줄 — 부모 grid 의 세 열(라벨 · 사람 · 시각)에 그대로 얹힌다.
 * Fragment 로 내보내야 자식이 부모 격자의 칸을 직접 차지해 줄끼리 열이 맞는다.
 */
function MetaRow({ label, who, at }) {
    return (
        <>
            <Typography sx={{ fontSize: UI.meta, color: "#94a3b8" }}>{label}</Typography>
            <Stack direction="row" alignItems="center" gap={0.8}>
                <Box sx={{ width: 24, height: 24, borderRadius: "50%", bgcolor: "#e2e8f0",
                    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <PersonRoundedIcon sx={{ fontSize: 15, color: "#94a3b8" }} />
                </Box>
                <Typography noWrap sx={{ fontSize: UI.meta, color: "#475569", fontWeight: 600 }}>
                    {who || "—"}
                </Typography>
            </Stack>
            <Typography sx={{ fontSize: UI.meta, color: "#94a3b8" }}>
                {at ? dayjs(at).format("YY-MM-DD HH:mm") : "—"}
            </Typography>
        </>
    );
}

/**
 * 기간 한 줄 — 종일/여러 날/시간 일정에 따라 형태가 다르다.
 * 휴가는 startDate·endDate(날짜)만 오고, 일반 일정은 startAt·endAt(일시)로 온다.
 */
function periodText(ev) {
    if (ev.isLeave) {
        const s = ev.startDate, e = ev.endDate;
        if (!s) return "";
        return s === e || !e
            ? dayjs(s).format("YY-MM-DD (ddd)")
            : `${dayjs(s).format("YY-MM-DD (ddd)")} ~ ${dayjs(e).format("YY-MM-DD (ddd)")}`;
    }
    if (!ev.startAt) return "";
    const s = dayjs(ev.startAt), e = ev.endAt ? dayjs(ev.endAt) : null;
    if (ev.allDay) {
        return (!e || s.isSame(e, "day"))
            ? `${s.format("YY-MM-DD (ddd)")} · 종일`
            : `${s.format("YY-MM-DD (ddd)")} ~ ${e.format("YY-MM-DD (ddd)")} · 종일`;
    }
    if (!e) return s.format("YY-MM-DD (ddd) HH:mm");
    return s.isSame(e, "day")
        ? `${s.format("YY-MM-DD (ddd) HH:mm")} ~ ${e.format("HH:mm")}`
        : `${s.format("YY-MM-DD (ddd) HH:mm")} ~ ${e.format("YY-MM-DD (ddd) HH:mm")}`;
}
